import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Link as LinkIcon,
  Tag,
  FileText,
  Users,
  Mail,
  Plus,
  X,
  Check,
  Send,
  Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { useI18n } from '@/i18n';
import { useToast } from '@/hooks/use-toast';
import { validateEmail } from '@/lib/utils';
import { getGroupsForCreator, type GroupData } from '@/api/groups';
import { submitApp } from '@/api/apps';

export default function SubmitAppPage() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { toast } = useToast();

  // Groups data
  const [groups, setGroups] = useState<GroupData[]>([]);
  const [loadingGroups, setLoadingGroups] = useState(true);

  const [formData, setFormData] = useState({
    name: '',
    link: '',
    description: '',
    categoryTags: [] as string[],
    allowedGroups: [] as string[],
    allowedEmails: [] as string[],
  });

  const [newTag, setNewTag] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  // Fetch groups
  const fetchGroups = useCallback(async () => {
    try {
      setLoadingGroups(true);
      const result = await getGroupsForCreator();
      setGroups(result ?? []);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load groups';
      toast({
        title: t.common.error,
        description: message,
        variant: 'destructive',
      });
      setGroups([]);
    } finally {
      setLoadingGroups(false);
    }
  }, [toast, t.common.error]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  // Suggested category tags
  const suggestedTags = t.tags.suggested;

  const addTag = () => {
    if (newTag.trim() && !formData.categoryTags.includes(newTag.trim())) {
      setFormData({
        ...formData,
        categoryTags: [...formData.categoryTags, newTag.trim()],
      });
      setNewTag('');
    }
  };

  const removeTag = (tag: string) => {
    setFormData({
      ...formData,
      categoryTags: formData.categoryTags.filter((t) => t !== tag),
    });
  };

  const toggleGroup = (groupName: string) => {
    setFormData({
      ...formData,
      allowedGroups: formData.allowedGroups.includes(groupName)
        ? formData.allowedGroups.filter((g) => g !== groupName)
        : [...formData.allowedGroups, groupName],
    });
  };

  const addEmail = () => {
    if (!validateEmail(newEmail)) {
      toast({
        title: t.common.error,
        description: t.validation.invalidEmail,
        variant: 'destructive',
      });
      return;
    }
    if (!formData.allowedEmails.includes(newEmail)) {
      setFormData({
        ...formData,
        allowedEmails: [...formData.allowedEmails, newEmail],
      });
      setNewEmail('');
    }
  };

  const removeEmail = (email: string) => {
    setFormData({
      ...formData,
      allowedEmails: formData.allowedEmails.filter((e) => e !== email),
    });
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = t.validation.appNameRequired;
    }

    if (!formData.link.trim()) {
      newErrors.link = t.validation.appLinkRequired;
    } else if (!formData.link.startsWith('http')) {
      newErrors.link = t.validation.invalidUrl;
    }

    if (!formData.description.trim()) {
      newErrors.description = t.validation.appDescRequired;
    }

    if (formData.allowedGroups.length === 0 && formData.allowedEmails.length === 0) {
      newErrors.permissions = t.validation.permissionsRequired;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    try {
      setSubmitting(true);
      await submitApp({
        name: formData.name,
        link: formData.link,
        description: formData.description,
        categoryTags: formData.categoryTags,
        allowedGroups: formData.allowedGroups,
        allowedEmails: formData.allowedEmails,
        version: '1.0.0',
      });
      toast({
        title: t.common.success,
        description: t.creator.submitSuccess,
      });
      navigate('/creator');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to submit app';
      toast({
        title: t.common.error,
        description: message,
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-8 max-w-3xl mx-auto">
      <div className="mb-8">
        <Link
          to="/creator"
          className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          {t.creator.backToMyApps}
        </Link>
        <h1 className="text-2xl font-bold text-gray-900">{t.creator.submitNewAppTitle}</h1>
        <p className="text-gray-500 mt-1">{t.creator.submitNewAppDesc}</p>
      </div>

      <div className="space-y-6">
        {/* Basic Info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              {t.creator.basicInfo}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Input
              label={t.creator.appName}
              placeholder={t.creator.appNamePlaceholder}
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              error={errors.name}
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                {t.creator.appLink}
              </label>
              <div className="relative">
                <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="https://estha.ai/apps/your-app"
                  value={formData.link}
                  onChange={(e) => setFormData({ ...formData, link: e.target.value })}
                  className="pl-10"
                  error={errors.link}
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {t.creator.appLinkHint}
              </p>
            </div>
            <Textarea
              label={t.creator.appDescription}
              placeholder={t.creator.appDescriptionPlaceholder}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={4}
              error={errors.description}
            />
          </CardContent>
        </Card>

        {/* Category Tags */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Tag className="w-5 h-5" />
              {t.creator.categoryTags}
            </CardTitle>
            <CardDescription>{t.creator.categoryTagsDesc}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2 mb-4">
              {formData.categoryTags.map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm"
                >
                  {tag}
                  <button onClick={() => removeTag(tag)} className="hover:text-green-900">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>

            <div className="flex gap-2 mb-4">
              <Input
                placeholder={t.creator.enterTag}
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                className="flex-1"
              />
              <Button variant="outline" onClick={addTag}>
                <Plus className="w-4 h-4" />
              </Button>
            </div>

            <div>
              <p className="text-sm text-gray-500 mb-2">{t.creator.suggestedTags}:</p>
              <div className="flex flex-wrap gap-2">
                {suggestedTags
                  .filter((tag) => !formData.categoryTags.includes(tag))
                  .map((tag) => (
                    <button
                      key={tag}
                      onClick={() =>
                        setFormData({
                          ...formData,
                          categoryTags: [...formData.categoryTags, tag],
                        })
                      }
                      className="px-3 py-1 text-sm bg-gray-100 text-gray-600 rounded-full hover:bg-gray-200"
                    >
                      + {tag}
                    </button>
                  ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Permissions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              {t.creator.visibleScope}
            </CardTitle>
            <CardDescription>{t.creator.visibleScopeDesc}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Groups */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t.creator.selectGroups}
              </label>
              <div className="flex flex-wrap gap-2 p-4 border border-gray-200 rounded-lg max-h-48 overflow-y-auto">
                {loadingGroups ? (
                  <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                ) : groups.length === 0 ? (
                  <p className="text-sm text-gray-500">{t.admin.noGroups}</p>
                ) : (
                  groups.map((group) => (
                    <button
                      key={group.id}
                      onClick={() => toggleGroup(group.name)}
                      className={`inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-full transition-colors ${
                        formData.allowedGroups.includes(group.name)
                          ? 'bg-blue-100 text-blue-700 border border-blue-300'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {formData.allowedGroups.includes(group.name) && (
                        <Check className="w-3 h-3" />
                      )}
                      <Users className="w-3 h-3" />
                      {group.name}
                    </button>
                  ))
                )}
              </div>
            </div>

            {/* Individual emails */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t.creator.addIndividuals}
              </label>
              <div className="flex gap-2 mb-3">
                <div className="relative flex-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="user@company.com"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addEmail())}
                    className="pl-10"
                  />
                </div>
                <Button variant="outline" onClick={addEmail}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>

              {formData.allowedEmails.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {formData.allowedEmails.map((email) => (
                    <span
                      key={email}
                      className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm"
                    >
                      <Mail className="w-3 h-3" />
                      {email}
                      <button onClick={() => removeEmail(email)} className="hover:text-green-900">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {errors.permissions && (
              <p className="text-sm text-red-600">{errors.permissions}</p>
            )}
          </CardContent>
        </Card>

        {/* Submit */}
        <div className="flex items-center justify-end gap-4">
          <Link to="/creator">
            <Button variant="outline">{t.common.cancel}</Button>
          </Link>
          <Button onClick={handleSubmit} disabled={submitting}>
            {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            <Send className="w-4 h-4 mr-2" />
            {t.creator.submitForReview}
          </Button>
        </div>
      </div>
    </div>
  );
}
